<template>
  <div v-if="tabs.length > 0" class="container-settingsview">
    <TabsWithValue :tabs="tabs" :tab-active-value="activeTab" @tabClicked="selectTab" />
    <div class="container-settings">
      <AuxiliaryDisplayControlScreen v-if="activeTab === 'SCREEN'" v-model="screenRemotePreference" type="remote" />
      <AuxiliaryDisplayControlSubtitle
        v-if="activeTab === 'SUBTITLE'"
        v-model="subtitleRemotePreference"
        type="remote"
      />
    </div>

    <div class="footer">
      <ButtonProcess full-width :disabled="!isApplyEnabled" @click="applyPreference">
        {{ t('screen_apply_setting') }}
      </ButtonProcess>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import AuxiliaryDisplayControlScreen from '@/components/domains/chat/chat-general/AuxiliaryDisplayControlScreen.vue';
import AuxiliaryDisplayControlSubtitle from '@/components/domains/chat/chat-general/AuxiliaryDisplayControlSubtitle.vue';
import ButtonProcess from '@/components/shared/base/ButtonProcess.vue';
import TabsWithValue from '@/components/shared/composite/TabsWithValue.vue';
import { useChatPreferences } from '@/composables/shared/useChatPreferences';
import { useCallStore } from '@/stores/call';
import { useChatRemotePreferenceStore } from '@/stores/chatPreference';
import { useSocketStore } from '@/stores/socket';
import { useToastStore } from '@/stores/toast';
import { AuxiliaryDisplay, TabItem } from '@/types/components';

const socketStore = useSocketStore();
const toastStore = useToastStore();
const callStore = useCallStore();
const chatRemotePreferenceStore = useChatRemotePreferenceStore();

const { screenRemotePreference, subtitleRemotePreference } = storeToRefs(chatRemotePreferenceStore);

const { t } = useI18n();
const { isValidPreference, getValidationError } = useChatPreferences();

const activeTab = ref<AuxiliaryDisplay>();

const PREFERENCE_MAP = {
  SCREEN: {
    key: 'screen',
    attributeName: 'screenAttribute',
    getAttribute: () => screenRemotePreference.value,
  },
  SUBTITLE: {
    key: 'subtitle',
    attributeName: 'subtitleAttribute',
    getAttribute: () => subtitleRemotePreference.value,
  },
} as const;

const tabs = computed<TabItem[]>(() => {
  const { screenMode, subtitleMode } = callStore.qrCodeInfo.options;

  return [
    screenMode && { value: 'SCREEN', label: t('label_screen_mode') },
    subtitleMode && { value: 'SUBTITLE', label: t('label_subtitle_mode') },
  ].filter(Boolean) as TabItem[];
});

const isApplyEnabled = computed<boolean>(() => {
  if (activeTab.value === 'SCREEN') {
    return !getValidationError(screenRemotePreference.value);
  } else if (activeTab.value === 'SUBTITLE') {
    return !getValidationError(subtitleRemotePreference.value);
  }
  return true;
});

const initTab = () => {
  if (tabs.value.length <= 0) {
    return;
  }
  const [{ value }] = tabs.value;
  activeTab.value = value;
};

const selectTab = (tab: AuxiliaryDisplay) => {
  activeTab.value = tab;
};

const applyPreference = () => {
  if (!activeTab.value) {
    return;
  }

  const { attributeName, getAttribute } = PREFERENCE_MAP[activeTab.value];
  const attribute = getAttribute();

  if (isValidPreference(attribute)) {
    socketStore.sendMessageFc({ [attributeName]: attribute });
    toastStore.info(t('screen_apply_all_success'));
  }
};

onMounted(() => {
  initTab();
});
</script>

<style scoped lang="scss">
.container-settingsview {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  width: 100%;
  height: 100%;
  padding: 1rem 1rem 0 1rem;
  gap: 0.5rem;
  overflow-y: auto;
}

.container-settings {
  width: 100%;
  height: 100%;
}

.footer {
  position: sticky;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  bottom: 0;
  width: 100%;
  padding: 0.5rem 0 1rem 0;
  background-color: var(--surface-default);
}

.divider {
  background-color: var(--border-dim);
}
</style>
